import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the P146-REV1 Backoffice Matching Recommendation
// API, plus the one pre-existing P106 Matching Queue route this console reuses
// to build its job queue (GET .../matching-queue/jobs already returns exactly
// the ready jobs this screen needs - a second job-list endpoint would have been
// duplicate backend surface for the same rows).
//
// Visibility scoping (Agent/PLC/SuperAgent/SuperAdmin) always happens on the
// backend from the caller's own session, never from a value this file sends.
// An agentId passed here is only ever a narrowing filter inside what the caller
// can already see.
//
// This file exposes two writes: approveMatchingRecommendation (send a worker to
// the employer) and ensureEmployerLink (get that recommendation's public resume
// link, so an operator can copy it into LINE or e-mail).
//
// THE EMPLOYER'S OWN ACCEPT AND REJECT ARE NOT HERE, and must never be added.
// They live on the unauthenticated public routes in ../public/publicResumeApi.js,
// because they are authorized by possession of the link and by nothing else.
// Calling them through this module would attach the operator's session token to
// an employer's decision - which is precisely the confusion between "we sent
// it" and "they agreed" that the whole flow exists to keep apart.

const RECOMMENDATIONS_PATH = "/backoffice/matching-recommendations";
const READY_JOBS_PATH = "/backoffice/thailand-jobs/matching-queue/jobs";

// Exactly the param names BackofficeMatchingQueueController.ListReadyJobs
// declares. Anything else passed in is silently dropped rather than forwarded,
// so a typo'd key can never reach the backend as an unfiltered pass-through.
const JOB_LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "search",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "agentId",
  "employerId",
  "province",
  "jobStatus",
];

// Exactly the param names
// BackofficeMatchingRecommendationController.ListCandidates declares.
const CANDIDATE_PARAM_KEYS = ["page", "pageSize", "search", "nationality", "skillCode", "agentId"];

const APPROVAL_LIST_PARAM_KEYS = ["jobId", "page", "pageSize"];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

/** The job queue: every job already marked ReadyForMatching that the caller can see. */
export function listRecommendationJobs(params = {}) {
  return apiClient.get(`${READY_JOBS_PATH}${buildQuery(JOB_LIST_PARAM_KEYS, params)}`);
}

// Requires a real, non-empty jobId - this file never issues a request for a
// missing/blank id (the caller only calls this once a job is actually selected).
export function listRecommendationCandidates(jobId, params = {}) {
  if (!jobId) {
    throw new Error("listRecommendationCandidates requires a jobId");
  }
  return apiClient.get(
    `${RECOMMENDATIONS_PATH}/jobs/${encodeURIComponent(jobId)}/candidates${buildQuery(CANDIDATE_PARAM_KEYS, params)}`,
  );
}

export function listMatchingRecommendations(params = {}) {
  return apiClient.get(`${RECOMMENDATIONS_PATH}${buildQuery(APPROVAL_LIST_PARAM_KEYS, params)}`);
}

/**
 * The single write this console performs: an admin approving one worker for
 * one job so the employer can be informed.
 *
 * The body carries only the job, the worker and an optional note. It never
 * sends a score - the backend recomputes and snapshots its own, so a stale or
 * tampered client score can never become the recorded basis of a decision.
 */
export function approveMatchingRecommendation({ jobId, workerId, approvalNote }) {
  if (!jobId || !workerId) {
    throw new Error("approveMatchingRecommendation requires both a jobId and a workerId");
  }
  return apiClient.post(RECOMMENDATIONS_PATH, {
    jobId,
    workerId,
    approvalNote: approvalNote ?? null,
  });
}

/**
 * The public resume link for one recommendation, for the operator to copy and
 * send to the employer out of band.
 *
 * A POST, and idempotent: the backend mints a token only when the row has none
 * (rows approved before the link existed), and returns the same one every time
 * after that. Pressing "copy link" twice must not invalidate the link already
 * pasted into somebody's LINE chat.
 */
export function ensureEmployerLink(recommendationId) {
  if (!recommendationId) {
    throw new Error("ensureEmployerLink requires a recommendationId");
  }
  return apiClient.post(`${RECOMMENDATIONS_PATH}/${encodeURIComponent(recommendationId)}/employer-link`, {});
}
